import { useCallback, useEffect, useState } from "react";
import { api, API_URL, BACKEND_URL } from "@/lib/api";

/**
 * Public connectivity diagnostic — safe to visit from any device/network.
 * Verifies that the same JS bundle running in the visitor's browser can
 * (a) resolve to a backend, (b) receive a health JSON response, and
 * (c) send a cross-origin POST successfully. Used to triage "Network Error"
 * reports without needing DevTools.
 */
export default function Diagnostic() {
    const [health, setHealth] = useState(null);
    const [echo, setEcho] = useState(null);
    const [healthErr, setHealthErr] = useState(null);
    const [echoErr, setEchoErr] = useState(null);
    const [running, setRunning] = useState(false);

    const runChecks = useCallback(async () => {
        setRunning(true);
        setHealth(null); setEcho(null); setHealthErr(null); setEchoErr(null);
        try {
            const { data } = await api.get("/public/health");
            setHealth(data);
        } catch (e) { setHealthErr(e?.message || String(e)); }
        try {
            const { data } = await api.post("/public/echo", {
                probe: "diagnostic",
                at: new Date().toISOString(),
                ua: (typeof navigator !== "undefined" && navigator.userAgent) || "unknown",
            });
            setEcho(data);
        } catch (e) { setEchoErr(e?.message || String(e)); }
        setRunning(false);
    }, []);

    useEffect(() => { runChecks(); }, [runChecks]);

    const pageOrigin = typeof window !== "undefined" && window.location ? window.location.origin : "—";

    return (
        <div className="min-h-screen bg-[color:var(--gsf-soft-green)] p-6" data-testid="diagnostic-page">
            <div className="max-w-3xl mx-auto gsf-card p-8">
                <div className="gsf-eyebrow">Connectivity Diagnostic</div>
                <h1 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold" data-testid="diagnostic-title">
                    Is the Global Seed Foundation service reachable?
                </h1>
                <p className="text-sm text-gray-600 mt-2">
                    This page automatically checks whether your device can reach the Foundation's online service. If any check below is red, please screenshot this page and send it to us on WhatsApp <b>+263 78 265 8473</b> so we can help.
                </p>

                <dl className="mt-6 grid grid-cols-3 gap-y-3 text-sm">
                    <dt className="text-gray-500">Page origin</dt>
                    <dd className="col-span-2 font-mono break-all" data-testid="diag-origin">{pageOrigin}</dd>

                    <dt className="text-gray-500">Backend base URL</dt>
                    <dd className="col-span-2 font-mono break-all" data-testid="diag-backend">{BACKEND_URL || "(same-origin)"}</dd>

                    <dt className="text-gray-500">API base</dt>
                    <dd className="col-span-2 font-mono break-all" data-testid="diag-api">{API_URL}</dd>
                </dl>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CheckCard
                        title="Backend health"
                        subtitle="GET /api/public/health"
                        ok={!!health && !healthErr}
                        err={healthErr}
                        data={health}
                        testid="diag-health"
                    />
                    <CheckCard
                        title="Cross-origin POST"
                        subtitle="POST /api/public/echo"
                        ok={!!echo && !echoErr}
                        err={echoErr}
                        data={echo}
                        testid="diag-echo"
                    />
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button onClick={runChecks} disabled={running}
                        className="gsf-btn-outline text-sm disabled:opacity-60"
                        data-testid="diag-rerun">
                        {running ? "Running checks…" : "Run checks again"}
                    </button>
                    <span className="text-xs text-gray-500">Tip: on mobile, tap and hold to save this page URL, or share it with our team.</span>
                </div>
            </div>
        </div>
    );
}

function CheckCard({ title, subtitle, ok, err, data, testid }) {
    return (
        <div className={`rounded-lg border p-4 ${ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`} data-testid={testid}>
            <div className="font-medium text-sm text-[color:var(--gsf-charcoal)]">{title}</div>
            <div className="text-[11px] text-gray-500 font-mono">{subtitle}</div>
            <div className={`mt-2 text-xs font-medium ${ok ? "text-green-800" : "text-red-800"}`} data-testid={`${testid}-status`}>
                {ok ? "OK — service reachable" : (err || "Failed")}
            </div>
            {data && (
                <pre className="mt-2 text-[10px] max-h-56 overflow-auto bg-white/70 rounded p-2 border border-gray-200" data-testid={`${testid}-body`}>
                    {JSON.stringify(data, null, 2)}
                </pre>
            )}
        </div>
    );
}
